import { animate, motion, useIsPresent, useMotionValue, useMotionValueEvent, useReducedMotion, useTransform } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import type { ListItemRow } from '../../db/types';
import { IconCheck } from './icons';
import { ItemText } from './ItemText';

/** Przesunięcie (część szerokości wiersza), po którym puszczenie odhacza pozycję. */
const COMMIT_RATIO = 0.35;
/** Szybkie machnięcie odhacza nawet przy krótkim przesunięciu. */
const FLICK_VELOCITY = 600;
const FLICK_MIN_PX = 48;
const LONG_PRESS_MS = 450;
const LONG_PRESS_SLOP_PX = 8;

export type SwipeStatus = 'cart' | 'missing';

interface Props {
  item: ListItemRow;
  /** false = zapis się nie udał, wiersz wraca na miejsce. */
  onCommit: (status: SwipeStatus) => Promise<boolean>;
  /** Dotknięcie albo przytrzymanie: arkusz z opcjami pozycji. */
  onOpen: () => void;
  /** Dopisana przez domownika w trakcie zakupów. */
  fresh?: boolean;
  /** Miejsce na trasie zgadnięte (znak ✦). */
  guessed?: boolean;
  /** Dodatkowy blok pod nazwą (propozycja zamiennika z przyciskami). */
  banner?: React.ReactNode;
  /** Ostatnia cena z paragonów. */
  price?: string | null;
  promo?: string | null;
}

/**
 * Wiersz do odhaczania jedną ręką: w prawo = w koszyku, w lewo = brak, kółko po prawej = w koszyku,
 * dotknięcie lub przytrzymanie = więcej opcji. Przeciągnięcie w pionie zostaje przewijaniem strony (touch-action: pan-y).
 */
export function SwipeRow({ item, onCommit, onOpen, fresh = false, guessed = false, banner, price = null, promo = null }: Props) {
  const x = useMotionValue(0);
  const rowRef = useRef<HTMLDivElement>(null);
  const widthRef = useRef(0);
  const reduceMotion = useReducedMotion();
  const isPresent = useIsPresent();
  const cartOpacity = useTransform(x, [0, 40], [0, 1]);
  const missOpacity = useTransform(x, [-40, 0], [1, 0]);
  const [armed, setArmed] = useState<SwipeStatus | null>(null);
  // Po przytrzymaniu wyłączamy przeciąganie do końca gestu, żeby wiersz nie jeździł pod arkuszem.
  const [held, setHeld] = useState(false);
  // maxDx: największe przesunięcie w bieżącym geście — odróżnia tapnięcie/przytrzymanie od przeciągnięcia.
  const gesture = useRef({ maxDx: 0, longPressed: false, committing: false, pointerType: '' });
  const press = useRef<{ x: number; y: number; timer: number } | null>(null);

  // Cofnięte odhaczenie przywraca ten sam wiersz (AnimatePresence) — wraca na miejsce.
  useEffect(() => {
    if (!isPresent) return;
    gesture.current.committing = false;
    x.set(0);
  }, [isPresent, x]);

  useEffect(() => () => cancelPress(), []);

  useMotionValueEvent(x, 'change', (v) => {
    if (gesture.current.committing) return;
    gesture.current.maxDx = Math.max(gesture.current.maxDx, Math.abs(v));
    const t = (widthRef.current || 320) * COMMIT_RATIO;
    setArmed(v > t ? 'cart' : v < -t ? 'missing' : null);
  });

  function cancelPress() {
    if (!press.current) return;
    window.clearTimeout(press.current.timer);
    press.current = null;
  }

  function openMore() {
    gesture.current.longPressed = true;
    setHeld(true);
    navigator.vibrate?.(10);
    swallowClickAfterRelease(() => setHeld(false));
    onOpen();
  }

  async function commit(status: SwipeStatus) {
    gesture.current.committing = true;
    setArmed(status);
    navigator.vibrate?.(15);
    const w = rowRef.current?.offsetWidth ?? window.innerWidth;
    const target = status === 'cart' ? w : -w;
    if (reduceMotion) x.set(target);
    else await animate(x, target, { duration: 0.14, ease: 'easeOut' });
    if (await onCommit(status)) return;
    gesture.current.committing = false;
    setArmed(null);
    void animate(x, 0);
  }

  const settle = () => void animate(x, 0, { type: 'spring', stiffness: 600, damping: 40 });

  return (
    <div className="relative overflow-hidden border-b border-stone-200 bg-stone-100 select-none [-webkit-touch-callout:none] dark:border-stone-800 dark:bg-stone-900">
      {/* Tło odsłaniane przy przesuwaniu */}
      <motion.div
        aria-hidden
        style={{ opacity: cartOpacity }}
        className="absolute inset-0 flex items-center gap-2 bg-brand-600 pl-5 font-semibold text-white"
      >
        <span className={`flex items-center gap-2 transition-transform ${armed === 'cart' ? 'scale-110' : ''}`}>
          <IconCheck /> W koszyku
        </span>
      </motion.div>
      <motion.div
        aria-hidden
        style={{ opacity: missOpacity }}
        className="absolute inset-0 flex items-center justify-end bg-miss-500 pr-5 font-semibold text-white"
      >
        <span className={`transition-transform ${armed === 'missing' ? 'scale-110' : ''}`}>Brak</span>
      </motion.div>

      <motion.div
        ref={rowRef}
        drag={held ? false : 'x'}
        dragDirectionLock
        dragMomentum={false}
        style={{ x, touchAction: 'pan-y' }}
        tabIndex={0}
        aria-label={item.name}
        aria-description="Przesuń w prawo: w koszyku, w lewo: brak. Dotknij, by zobaczyć więcej opcji."
        className="relative flex min-h-14 items-center gap-2 bg-white pl-4 outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-inset dark:bg-stone-900"
        onPointerDownCapture={(e) => {
          gesture.current.maxDx = 0;
          gesture.current.longPressed = false;
          gesture.current.pointerType = e.pointerType;
        }}
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          cancelPress();
          const timer = window.setTimeout(() => {
            press.current = null;
            if (gesture.current.maxDx <= LONG_PRESS_SLOP_PX) openMore();
          }, LONG_PRESS_MS);
          press.current = { x: e.clientX, y: e.clientY, timer };
        }}
        onPointerMove={(e) => {
          const p = press.current;
          if (p && Math.hypot(e.clientX - p.x, e.clientY - p.y) > LONG_PRESS_SLOP_PX) cancelPress();
        }}
        onPointerUp={cancelPress}
        onPointerCancel={cancelPress}
        onClick={() => {
          // Dotknięcie bez przesunięcia; po przytrzymaniu kliknięcie i tak zostaje połknięte.
          const g = gesture.current;
          if (g.maxDx > LONG_PRESS_SLOP_PX || g.longPressed || g.committing) return;
          onOpen();
        }}
        onContextMenu={(e) => {
          // Prawy klik na komputerze = przytrzymanie; na dotyku menu przeglądarki tylko blokujemy.
          e.preventDefault();
          if (gesture.current.pointerType === 'mouse') onOpen();
        }}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === 'ContextMenu') {
            e.preventDefault();
            onOpen();
          }
        }}
        onDragStart={() => {
          // Bez anulowania przytrzymania: drag startuje już po 3 px, a palec zawsze trochę drży.
          widthRef.current = rowRef.current?.offsetWidth ?? 0;
        }}
        onDragEnd={(_e, info) => {
          if (gesture.current.committing) return;
          if (gesture.current.longPressed) return settle();
          const dx = x.get();
          const vx = info.velocity.x;
          const w = widthRef.current || window.innerWidth;
          const flick = Math.abs(vx) > FLICK_VELOCITY && Math.abs(dx) > FLICK_MIN_PX && Math.sign(vx) === Math.sign(dx);
          if (Math.abs(dx) > w * COMMIT_RATIO || flick) void commit(dx > 0 ? 'cart' : 'missing');
          else settle();
        }}
      >
        <ItemText item={item} fresh={fresh} guessed={guessed} price={price} promo={promo}>
          {banner && (
            // Przyciski w banerze: bez przytrzymania, bez kliknięcia po przeciągnięciu wiersza i bez otwierania arkusza.
            <div
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onClickCapture={(e) => {
                const g = gesture.current;
                if (g.maxDx > LONG_PRESS_SLOP_PX || g.longPressed || g.committing) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            >
              {banner}
            </div>
          )}
        </ItemText>
        <button
          type="button"
          aria-label={`W koszyku: ${item.name}`}
          className="group flex size-14 shrink-0 items-center justify-center"
          // Bez przytrzymania z kółka — React zatrzymuje bąbelkowanie do wiersza.
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            // Kliknięcie kółka nie dochodzi do wiersza (tam otwiera arkusz).
            e.stopPropagation();
            const g = gesture.current;
            if (g.maxDx > LONG_PRESS_SLOP_PX || g.longPressed || g.committing) return;
            void commit('cart');
          }}
        >
          <span className="flex size-8 items-center justify-center rounded-full border-2 border-stone-300 text-transparent group-active:border-brand-600 group-active:bg-brand-600 group-active:text-white dark:border-stone-600">
            <IconCheck className="size-4" />
          </span>
        </button>
      </motion.div>
    </div>
  );
}

/**
 * Po przytrzymaniu otwiera się arkusz; podniesienie palca wygenerowałoby „click” w jego tło i od razu go zamknęło.
 * Połykamy jedno kliknięcie tuż po puszczeniu.
 */
function swallowClickAfterRelease(onRelease: () => void) {
  const stop = (ev: Event) => {
    ev.preventDefault();
    ev.stopPropagation();
  };
  const release = (ev: PointerEvent) => {
    window.removeEventListener('pointerup', release, true);
    window.removeEventListener('pointercancel', release, true);
    onRelease();
    if (ev.type !== 'pointerup') return;
    window.addEventListener('click', stop, { capture: true, once: true });
    window.setTimeout(() => window.removeEventListener('click', stop, true), 400);
  };
  window.addEventListener('pointerup', release, true);
  window.addEventListener('pointercancel', release, true);
}
